import { useState } from 'react';
import { createPortal } from 'react-dom';
import { DollarSign } from 'lucide-react';
import { TakePaymentModal } from '../TakePaymentModal';
import { JobDTO } from '@/types';
import { useJobPayable } from '@/hooks/usePayable';

interface TakePaymentButtonProps {
    job: JobDTO;
    onPaymentComplete?: (updatedJob?: JobDTO) => void;
    disabled?: boolean;
    className?: string;
    /** Optional custom render function. Receives onClick handler and disabled state. */
    renderCustom?: (onClick: () => void, isDisabled: boolean) => React.ReactNode;
}

/**
 * Reusable button to initiate the unified payment modal for a job.
 *
 * Opens a modal where users can either:
 *   1. Record a manual payment (Cash, Check, ACH, Zelle, Credit Card)
 *   2. Charge via Stripe (live card entry)
 */
export function TakePaymentButton({
    job,
    onPaymentComplete,
    disabled = false,
    className = '',
    renderCustom,
}: TakePaymentButtonProps) {
    const [showModal, setShowModal] = useState(false);
    const payable = useJobPayable(job, onPaymentComplete);

    const handleClick = () => {
        setShowModal(true);
    };

    const isDisabled = disabled;

    return (
        <>
            {renderCustom ? (
                renderCustom(handleClick, isDisabled)
            ) : (
                <button
                    type="button"
                    onClick={handleClick}
                    disabled={isDisabled}
                    title="Take payment"
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors
                ${isDisabled
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-primary-600 text-white hover:bg-primary-700'
                        } ${className}`}
                >
                    <DollarSign className="h-4 w-4" />
                    Take Payment
                </button>
            )}

            {createPortal(
                <TakePaymentModal
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    payable={payable}
                />,
                document.body
            )}
        </>
    );
}
